/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { getContext } from "redux-saga/effects";
import { call } from "typed-redux-saga";
import { CMSClientType } from "@/cms-api";
import { ProcessAction } from "@centralwebteam/narwhal";

export const data = {
  fetchOffsetAdjustment: function* fetchOffsetAdjustment({
    from,
    to,
    take,
  }: {
    from: string;
    to: string;
    take: number;
  }) {
    const client: CMSClientType = yield getContext("client");
    const allProcessActions = yield* call(
      () =>
        client.events.processActions.all({
          query: { from, to, take },
        }).promise
    );

    return allProcessActions.filter(containsRequiredProperties);
  },
};

// Validates that a process action has the minimum required properties to be displayed in its respective tab.

function containsRequiredProperties(processAction: ProcessAction): boolean {
  if (processAction.id === undefined || processAction.details === undefined) {
    return false;
  }

  const { details, grouping } = processAction;

  if (grouping === "Characteristic") {
    return containsCharacteristicRequiredProperties(details);
  } else if (grouping === "Job") {
    return containsJobRequiredProperties(details);
  } else if (grouping === "Machine") {
    return containsMachineRequiredProperties(details);
  }
  return containsCharacteristicRequiredProperties(details);
}

/*
 * Machine tab requires:
 * processTarget.name, offsetTarget.id, action.name, action.units
 */
function containsMachineRequiredProperties(
  details: ProcessAction["details"]
): boolean {
  if (!details) return false;

  const hasProcessTarget =
    details.processTarget !== undefined &&
    details.processTarget.name !== undefined;

  const hasOffsetTarget =
    details.offsetTarget !== undefined && details.offsetTarget.id !== undefined;

  const hasAction =
    details.action !== undefined &&
    details.action.name !== undefined &&
    details.action.units !== undefined;

  return hasProcessTarget && hasOffsetTarget && hasAction;
}

/*
 * Job tab requires machine properties plus job information
 */
function containsJobRequiredProperties(
  details: ProcessAction["details"]
): boolean {
  if (!containsMachineRequiredProperties(details)) {
    return false;
  }

  return details!.analysisTrigger !== undefined;
}

/*
 * Characteristic tab requires all properties including source machine and execution settings
 */
function containsCharacteristicRequiredProperties(
  details: ProcessAction["details"]
): boolean {
  if (!containsJobRequiredProperties(details)) {
    return false;
  }

  return (
    details!.sourceMachine !== undefined &&
    details!.executionSettings !== undefined
  );
}
